// ── Contract domain types ──────────────────────────────────────────────────

export type ContractStatus = "draft" | "sent" | "signed" | "void";
export type AmendmentStatus = "draft" | "sent" | "signed" | "void";
export type NetTerms =
  | "Due on Receipt"
  | "Net 15"
  | "Net 30"
  | "Net 60"
  | "Net 90";

export interface LineItem {
  description: string;
  qty: number;
  unitPrice: number;
}

export interface Amendment {
  id: number;
  title: string;
  body: string;
  newMonthlyValue: number;
  proratedAmount?: number;
  effectiveDate: string;
  status: AmendmentStatus;
  sentAt?: string;
  signedAt?: string;
  signingToken?: string;
}

export interface Contract {
  id: number;
  title: string;
  customerName: string;
  customerEmail: string;
  customerCompany?: string;
  stripeCustomerId?: string;
  status: ContractStatus;
  totalMonthlyValue: number;
  setupFee?: number;
  netTerms: NetTerms;
  serviceStartDate: string;
  serviceEndDate?: string;
  body: string;
  lineItemsJson?: string;
  sentAt?: string;
  signedAt?: string;
  signedByEmail?: string;
  signedByIp?: string;
  signingToken?: string;
  amendments: Amendment[];
  templateId?: number;
}

export interface ContractListItem {
  id: number;
  title: string;
  customerName: string;
  customerEmail: string;
  status: ContractStatus;
  totalMonthlyValue: number;
  serviceStartDate: string;
  sentAt?: string;
  signedAt?: string;
  amendments: Amendment[];
}

export interface ContractTemplate {
  id: number;
  name: string;
  body: string;
  netTerms: NetTerms;
}

// ── Create/Update payloads ────────────────────────────────────────────────

export interface ContractPayload {
  templateId?: number;
  title: string;
  body: string;
  customerName: string;
  customerEmail: string;
  customerCompany?: string;
  stripeCustomerId?: string;
  totalMonthlyValue: number;
  setupFee?: number;
  netTerms: NetTerms;
  serviceStartDate: string;
  serviceEndDate?: string;
  lineItemsJson?: string;
}

export interface AmendmentPayload {
  title: string;
  body: string;
  newMonthlyValue: number;
  proratedAmount?: number;
  effectiveDate: string;
  netTerms?: NetTerms;
}

export interface TemplatePayload {
  name: string;
  body: string;
  netTerms: NetTerms;
}

// ── Public signing types ──────────────────────────────────────────────────

export interface PublicContractData {
  id: number;
  title: string;
  customerName: string;
  customerEmail: string;
  customerCompany?: string;
  body: string;
  lineItemsJson?: string;
  totalMonthlyValue: number;
  setupFee?: number;
  netTerms: NetTerms;
  serviceStartDate: string;
  serviceEndDate?: string;
  status: ContractStatus;
  signedAt?: string;
  signedByEmail?: string;
  /** If present, this is an amendment signing page */
  amendmentId?: number;
  amendmentTitle?: string;
  amendmentBody?: string;
  amendmentStatus?: AmendmentStatus;
}

export const NET_TERMS_OPTIONS: NetTerms[] = [
  "Due on Receipt",
  "Net 15",
  "Net 30",
  "Net 60",
  "Net 90",
];

export const DEFAULT_CONTRACT_BODY = [
  "**MASTER SERVICES AGREEMENT**",
  "",
  'This Master Services Agreement ("Agreement") is entered into as of {{start_date}} between Velaro, Inc. ("Provider") and {{customer_company}} ("Customer").',
  "",
  "## 1. Services",
  "",
  "Provider agrees to provide the services described in the line items attached hereto, commencing {{start_date}}.",
  "",
  "## 2. Fees",
  "",
  "Customer agrees to pay ${{monthly_value}} per month, due {{net_terms}}.",
  "",
  "## 3. Term",
  "",
  "This Agreement shall commence on {{start_date}}{{end_date_clause}} and shall continue until terminated by either party with thirty (30) days written notice.",
  "",
  "## 4. Confidentiality",
  "",
  "Each party agrees to maintain the confidentiality of the other party's proprietary information.",
  "",
  "## 5. Limitation of Liability",
  "",
  "IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.",
  "",
  "---",
  "",
  "By signing below, both parties agree to the terms of this Agreement.",
  "",
  "Customer: **{{customer_name}}** ({{customer_company}})",
].join("\n");
